import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { RevealLines, FadeUp } from './Reveal';

/**
 * Hero — kinetic, editorial, with a subtle parallax on the clipped image.
 * Accepts a country prop (from COUNTRIES) or defaults to the global variant.
 */
export default function Hero({ country, global = false }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '-15%']);

  const overline = global ? 'Studio Legale · Verona, Italia' : country.hero.overline;
  const titleLines = global
    ? ['An Italian lawyer', 'on your side in Italy.']
    : country.hero.title;
  const lede = global
    ? 'An Italian law firm helping foreign businesses recover money from Italian debtors.'
    : country.hero.lede;
  const accent = global ? '#4A0404' : country.accent;

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full bg-ink-950 overflow-hidden pt-24 md:pt-32"
      style={{ position: 'relative' }}
      data-testid="hero-section"
    >
      {/* Overline row */}
      <div className="relative z-20 mx-auto max-w-[1600px] px-6 md:px-10">
        {!global && country?.nativeGreeting && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="mb-3"
            data-testid="hero-native-greeting"
          >
            <span className="font-serif italic text-white/70 text-xl md:text-2xl leading-none">
              {country.nativeGreeting}
            </span>
          </motion.div>
        )}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="flex items-center gap-4 text-white/50"
        >
          <span className="h-px w-8 bg-white/40" style={{ background: accent }} />
          <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.3em]">{overline}</span>
        </motion.div>
      </div>

      {/* Main grid */}
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-8 mt-8 md:mt-14">
        {/* Headline */}
        <motion.div style={{ y: textY }} className="col-span-12 lg:col-span-8">
          <h1 className="font-serif font-light text-white text-[13vw] md:text-[9vw] lg:text-[7.2vw] leading-[0.92] tracking-[-0.02em]">
            <RevealLines lines={titleLines} delay={0.35} stagger={0.14} />
          </h1>

          <FadeUp delay={1.1} className="mt-8 md:mt-12 max-w-2xl">
            <p className="text-base md:text-lg text-white/70 leading-relaxed">{lede}</p>
          </FadeUp>

          <FadeUp delay={1.35} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#claim-form"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-white text-black px-7 py-4 text-[11px] font-mono uppercase tracking-[0.25em] hover:bg-oxblood hover:text-white transition-colors duration-300"
            >
              Submit Your Claim for Review
              <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden>→</span>
            </a>
            <a
              href="#workflow"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 text-white/70 text-[11px] font-mono uppercase tracking-[0.25em] hover-underline"
            >
              How our workflow works
            </a>
          </FadeUp>
        </motion.div>

        {/* Clipped editorial image with parallax */}
        <div className="col-span-12 lg:col-span-4 relative min-h-[340px] lg:min-h-[520px]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, delay: 0.6 }}
            className="absolute inset-0 clip-diagonal overflow-hidden bg-ink-800"
            data-testid="hero-image"
          >
            <motion.img
              src="/hero-courthouse.jpg"
              alt="Institutional stair architecture"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-full object-cover grayscale contrast-110 brightness-90 will-change-transform"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-transparent" />
          </motion.div>
          {/* Country accent bar */}
          <div
            className="absolute -left-2 top-8 w-1 h-24 hidden lg:block"
            style={{ background: accent }}
            aria-hidden
          />
        </div>
      </div>

      {/* Bottom index row */}
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 mt-24 md:mt-32 pb-10 flex items-end justify-between text-white/40">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em]">
          <div>Pragmatic · Confidential · Institutional</div>
          <div className="mt-1 text-white/25">Verona · Italia · MMXXVI</div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.8 }}
          className="font-mono text-[10px] uppercase tracking-[0.3em]"
        >
          Scroll ↓
        </motion.div>
      </div>
    </section>
  );
}
